import { forwardRef } from 'react'

import { IconCircleDotted } from '@tabler/icons-react'
import styled, {
  css,
  keyframes,
  StyledComponent,
  StyledComponentProps,
} from 'styled-components'

import { colourOpacity, colourShift } from '@/utils/colourShift'

import { BuildPassTheme } from 'types/BuildPassTheme'

type InternalButtonProps = {
  size?: 'small' | 'medium' | 'large'
  shape?: 'rounded' | 'circle' | 'pill'
  block?: boolean
  negative?: boolean
  naked?: boolean
  iconColour?: string
  loading?: boolean
  whiteBg?: boolean
  children?: React.ReactNode
}

export const HEIGHTS = {
  small: '1.75rem',
  medium: '2.5rem',
  large: '3rem',
}

export const ButtonBase = styled.button<ButtonProps>`
  position: relative;
  height: ${({ size }) => HEIGHTS[size || 'medium']};
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  border: 1px solid transparent;
  padding-left: 1rem;
  padding-right: 1rem;
  background-color: transparent;
  transition: 0.2s;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }

  &:focus-visible::after {
    content: '';
    position: absolute;
    inset: -0.125rem;
    border: 0.125rem solid ${({ theme }) => theme.colour.secondary.base};
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }

  &:disabled {
    cursor: not-allowed;
  }

  & > svg {
    flex-shrink: 0;
    color: ${({ iconColour }) => iconColour || 'inherit'};
  }

  ${({ block }) =>
    block &&
    css`
      width: 100%;
    `}

  ${({ size }) =>
    size === 'large' &&
    css`
      height: ${HEIGHTS.large};
    `}

    ${({ shape }) =>
    shape === 'pill' &&
    css`
      border-radius: ${({ theme }) => theme.borderRadius.max};
      padding-left: 1.5rem;
      padding-right: 1.5rem;

      &:focus-visible::after {
        border-radius: ${({ theme }) => theme.borderRadius.max};
      }
    `}

    ${({ shape, size }) =>
    shape === 'circle' &&
    css`
      border-radius: 50%;
      width: ${HEIGHTS[size || 'medium']};
      height: ${HEIGHTS[size || 'medium']};
      padding-left: 0;
      padding-right: 0;

      &:focus-visible::after {
        border-radius: 50%;
      }
    `}

    ${({ whiteBg, theme }) =>
    whiteBg &&
    css`
      background-color: ${theme.colour.neutral.white} !important;
    `}
`

const PrimaryButton = styled(ButtonBase)`
  color: ${({ theme }) => theme.colour.theme.buttonTextColour};
  background-color: ${({ theme }) => theme.colour.theme.primaryColour};
  border: 1px solid ${({ theme }) => theme.colour.primary._500};

  &:hover {
    background-color: ${({ theme }) =>
      colourShift(theme.colour.theme.primaryColour, 20)};
  }

  &:active {
    background-color: ${({ theme }) =>
      colourShift(theme.colour.theme.primaryColour, -30)};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    background-color: ${({ theme }) => theme.colour.neutral.surface};
    border-color: ${({ theme }) => theme.colour.secondary._100};
  }

  ${({ negative }) =>
    negative &&
    css`
      color: ${({ theme }) => theme.colour.semantic.errorDark};
      background-color: ${({ theme }) => theme.colour.semantic.errorLight};
      border-color: ${({ theme }) => theme.colour.semantic.errorDark};

      &:hover {
        color: ${({ theme }) => theme.colour.secondary._900};
        background-color: ${({ theme }) => theme.colour.semantic.error};
      }

      &:active {
        color: ${({ theme }) => theme.colour.neutral.white};
        background-color: ${({ theme }) => theme.colour.semantic.errorDark};
      }
    `}
`

const SecondaryButton = styled(ButtonBase)`
  color: ${({ theme }) => theme.colour.secondary._900};
  border: 1px solid ${({ theme }) => theme.colour.secondary._900};
  background-color: ${({ theme, naked }) =>
    naked ? 'transparent' : theme.colour.neutral.white};

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }

  &:active {
    background-color: ${({ theme }) => theme.colour.secondary._50};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    border-color: ${({ theme }) => theme.colour.secondary._100};
    background-color: transparent;
  }

  ${({ negative }) =>
    negative &&
    css`
      color: ${({ theme }) => theme.colour.semantic.errorDark};
      border-color: ${({ theme }) => theme.colour.semantic.error};
      background-color: transparent;

      &:hover {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        border-color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: transparent;
      }

      &:active {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        border-color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.errorLight};
      }
    `}
`

const PrimaryTextButton = styled(ButtonBase)`
  color: ${({ theme }) => theme.colour.theme.primaryTextColour};

  &:hover {
    background-color: ${({ theme }) =>
      colourOpacity(theme.colour.theme.primaryColour, 0.15)};
  }

  &:active {
    background-color: ${({ theme }) =>
      colourOpacity(theme.colour.theme.primaryColour, 0.3)};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    background-color: transparent;
  }

  ${({ negative }) =>
    negative &&
    css`
      color: ${({ theme }) => theme.colour.semantic.errorDark};

      &:hover {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.errorLight};
      }

      &:active {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.error};
      }
    `}
`

const LinkTextButton = styled(ButtonBase)`
  /* Reset button styles */
  padding: 0;
  text-decoration: unset;
  font: inherit;
  height: unset;
  width: unset;
  border: none;
  background: none;

  cursor: pointer; /* Ensure it has a pointer cursor */
  text-decoration: none; /* Remove underline */
  color: ${({ theme }) => theme.colour.link.base};
  transition: color 0.3s ease; /* Add color transition for smooth animation */

  &:hover {
    color: ${({ theme }) =>
      colourOpacity(
        theme.colour.link.base,
        0.7
      )}; /* Lighten the text color on hover */
  }

  &:active {
    color: ${({ theme }) =>
      colourOpacity(
        theme.colour.link.base,
        0.5
      )}; /* Further lighten the text color on active */
  }
`

const SecondaryTextButton = styled(ButtonBase)`
  color: ${({ theme }) => theme.colour.secondary._700};

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
    border-color: ${({ theme }) => theme.colour.secondary._100};
  }

  &:active {
    background-color: ${({ theme }) => theme.colour.neutral.white};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    background-color: transparent;
    border-color: transparent;
  }

  ${({ negative }) =>
    negative &&
    css`
      color: ${({ theme }) => theme.colour.semantic.errorDark};

      &:hover {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.errorLight};
        border-color: transparent;
      }

      &:active {
        color: ${({ theme }) => theme.colour.semantic.errorDark};
        background-color: ${({ theme }) => theme.colour.semantic.error};
        border-color: transparent;
      }
    `}
`

const TertiaryButton = styled(ButtonBase)`
  color: ${({ theme }) => theme.colour.primary._600};
  background: ${({ theme }) => theme.colour.neutral.white};
  border: 1px solid ${({ theme }) => theme.colour.primary._600};

  &:hover {
    background: ${({ theme }) => theme.colour.primary._50};
  }

  &:active {
    background: ${({ theme }) => theme.colour.primary._50};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    background: ${({ theme }) => theme.colour.neutral.surface};
    border-color: ${({ theme }) => theme.colour.secondary._100};
  }
  ${({ negative }) =>
    negative &&
    css`
      color: ${({ theme }) => theme.colour.semantic.errorDark};
      background: ${({ theme }) => theme.colour.semantic.errorLight};
      border-color: ${({ theme }) => theme.colour.semantic.error};

      &:hover {
        background: ${({ theme }) => theme.colour.semantic.error}25;
      }

      &:active {
        background: ${({ theme }) => theme.colour.semantic.error}35;
      }
    `}
`

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const Loading = styled(IconCircleDotted)`
  animation: ${rotate} 3s linear infinite;
`

export const LoadingIcon = Loading

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

type HtmlButtonProps = Omit<
  StyledComponentProps<'button', BuildPassTheme, InternalButtonProps, never>,
  'ref'
>

export type Props = InternalButtonProps &
  HtmlButtonProps & {
    variant?:
      | 'primary'
      | 'secondary'
      | 'primary-text'
      | 'secondary-text'
      | 'link'
      | 'ghost'
      | 'text'
      | 'default'
      | 'dashed'
      | 'tertiary'
    icon?: React.ReactNode
    iconPlacement?: 'left' | 'right'
    disabled?: boolean
    naked?: boolean
    className?: string
  }

export type ButtonProps = Props

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = 'secondary',
      size = 'medium',
      shape = 'rounded',
      icon,
      iconPlacement = 'left',
      disabled,
      negative,
      naked,
      loading,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    let Wrapper: StyledComponent<'button', BuildPassTheme, ButtonProps, never>

    switch (variant) {
      case 'primary':
        Wrapper = PrimaryButton
        break
      case 'secondary':
        Wrapper = SecondaryButton
        break
      case 'primary-text':
        Wrapper = PrimaryTextButton
        break
      case 'secondary-text':
        Wrapper = SecondaryTextButton
        break
      // map legacy ant values
      case 'link':
        Wrapper = LinkTextButton
        break
      case 'ghost':
        Wrapper = SecondaryTextButton
        break
      case 'text':
        Wrapper = SecondaryTextButton
        break
      case 'dashed':
        Wrapper = SecondaryTextButton
        break
      case 'tertiary':
        Wrapper = TertiaryButton
        break
      case 'default':
        Wrapper = SecondaryTextButton
        break
    }

    const iconElement = loading ? <Loading size={16} /> : icon

    return (
      <Wrapper
        ref={ref}
        type="button"
        size={size}
        disabled={disabled || loading}
        negative={negative}
        naked={naked}
        shape={shape}
        className={className}
        {...rest}
      >
        {icon && iconPlacement === 'left' && (
          <IconWrapper translate="no">{iconElement}</IconWrapper>
        )}
        {children}
        {icon && iconPlacement === 'right' && (
          <IconWrapper translate="no">{iconElement}</IconWrapper>
        )}
      </Wrapper>
    )
  }
)

Button.displayName = 'Button'
